import { useEffect, useState } from 'react';
import { FlaskConical, AlertTriangle, XCircle, CheckCircle2, Info, ExternalLink, ListOrdered } from 'lucide-react';
import sprayService from '../../services/spray';
import type { MixCheckResult, MixFinding } from '../../types/spray';

interface MixCompatibilityPanelProps {
  chemicalIds: number[];
  /** Surfaces the verdict to the parent so a wizard can gate the next step. */
  onResult?: (result: MixCheckResult | null) => void;
}

function FindingRow({ f }: { f: MixFinding }) {
  const tone =
    f.severity === 'incompatible' ? 'text-red-700 bg-red-50 border-red-200'
    : f.severity === 'caution'    ? 'text-amber-700 bg-amber-50 border-amber-200'
    :                               'text-green-700 bg-green-50 border-green-200';

  const Icon =
    f.severity === 'incompatible' ? XCircle
    : f.severity === 'caution'    ? AlertTriangle
    :                               CheckCircle2;

  return (
    <div className={`rounded border px-3 py-2 ${tone}`}>
      <p className="text-sm font-medium flex items-start gap-1.5">
        <Icon className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{f.message}</span>
      </p>
      {f.reason && <p className="text-xs mt-1 opacity-90">{f.reason}</p>}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs opacity-75">
        {/* Where the rule came from matters: a label warning about an active
            ingredient is a different kind of claim from one farm's own note. */}
        {f.match_kind === 'active_ingredient' && f.matched_on && (
          <span>Matched on active ingredient: {f.matched_on}</span>
        )}
        {f.match_kind === 'moa_group' && f.matched_on && <span>Matched on {f.matched_on}</span>}
        {f.match_kind === 'formulation' && f.matched_on && <span>Applies to {f.matched_on}</span>}
        {f.scope === 'organisation' && <span>Your organisation&rsquo;s own record</span>}
        {f.scope === 'derived' && <span>Derived from the products&rsquo; stated pH ranges</span>}
        {f.source_url && (
          <a
            href={f.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 underline"
          >
            Source <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * Tank-mix compatibility for the products currently selected.
 *
 * Two things are shown, and only one of them is a warning: what is known
 * against the mix, and the order the products should go into the tank. Fill
 * order is shown whether or not anything was flagged, because the wrong order
 * will break a mix of entirely compatible products.
 *
 * The panel never says a mix is safe. "Nothing recorded against this mix" is
 * the strongest claim the data supports — for most pairs no rule exists, which
 * is simply the truth and not a clearance.
 */
function MixCompatibilityPanel({ chemicalIds, onResult }: MixCompatibilityPanelProps) {
  const [result, setResult]   = useState<MixCheckResult | null>(null);
  const [loading, setLoading] = useState(false);

  const key = chemicalIds.slice().sort((a, b) => a - b).join(',');

  useEffect(() => {
    const ids = key ? key.split(',').map(Number) : [];
    if (ids.length === 0) {
      setResult(null);
      onResult?.(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    sprayService.checkCompatibility(ids)
      .then(res => {
        if (cancelled) return;
        const data = res.success && res.data ? res.data : null;
        setResult(data);
        onResult?.(data);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // onResult is intentionally excluded — callers pass an inline closure and
    // including it would re-run the check on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!result && !loading) return null;

  const realFindings = (result?.findings ?? []).filter(f => f.severity !== 'compatible');
  const positives    = (result?.findings ?? []).filter(f => f.severity === 'compatible');
  const isMix        = (result?.chemicals.length ?? 0) > 1;

  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <FlaskConical className="h-5 w-5 text-primary-600" />
        <h3 className="font-medium text-sm text-neutral-800">Tank-mix compatibility</h3>
        {loading && <span className="text-xs text-neutral-400">checking…</span>}
      </div>

      {result && isMix && realFindings.length === 0 && (
        <p className="text-sm text-neutral-600 flex items-start gap-1.5">
          <Info className="h-4 w-4 text-neutral-400 shrink-0 mt-0.5" />
          <span>
            Nothing is recorded against this mix across {result.checked_pairs}{' '}
            {result.checked_pairs === 1 ? 'pair' : 'pairs'}. That means no rule has been entered —
            not that the mix has been cleared. Jar-test anything you have not mixed before.
          </span>
        </p>
      )}

      {realFindings.map((f, i) => <FindingRow key={`${f.a_chemical_id}-${f.b_chemical_id}-${i}`} f={f} />)}
      {positives.map((f, i) => <FindingRow key={`ok-${f.a_chemical_id}-${f.b_chemical_id}-${i}`} f={f} />)}

      {result?.jar_test_required && (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-sm font-medium text-amber-800 flex items-start gap-1.5">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            Jar test required before mixing
          </p>
          <p className="text-xs text-amber-700 mt-0.5">
            {result.jar_test_products.map(p => p.product_name).join(', ')}
            {result.jar_test_products.length === 1 ? ' carries' : ' carry'} a label instruction to
            jar-test the mix first.
          </p>
        </div>
      )}

      {result && isMix && result.fill_order.length > 1 && (
        <div>
          <p className="text-xs font-medium text-neutral-700 flex items-center gap-1.5 mb-1.5">
            <ListOrdered className="h-3.5 w-3.5" />
            Fill order
          </p>
          <ol className="space-y-1">
            {result.fill_order.map(entry => (
              <li key={entry.chemical_id} className="flex items-center gap-2 text-sm text-neutral-700">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-neutral-100 text-xs font-semibold text-neutral-600 shrink-0">
                  {entry.position}
                </span>
                <span className="truncate">{entry.product_name}</span>
                <span className="text-xs text-neutral-400 truncate">
                  {entry.group_label}
                  {/* Say so when the slot was guessed from the formulation code
                      rather than set on the product — it is a weaker claim. */}
                  {entry.inferred && ' · inferred'}
                </span>
              </li>
            ))}
          </ol>
          <p className="text-xs text-neutral-500 mt-1.5">
            Add each product fully dispersed before the next, with the agitator running.
          </p>
        </div>
      )}
    </div>
  );
}

export default MixCompatibilityPanel;
